import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Col, Row, Card, Spinner, Button } from "react-bootstrap";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell, LabelList } from "recharts";
import Footer from "../layouts/Footer";
import Sidebar from "../layouts/Sidebar";
import vindrApi from "../utils/vindrapi";
import authApi from "../utils/authApi";
import UserMenu from "../components/UserMenu";
import DataTable from "react-data-table-component";

const COUNTRY_CODES = {
	Estonia: "EE",
	Latvia: "LV",
	Norway: "NO",
	Sweden: "SE"
};

const COUNTRY_FROM_CODE = {
	EE: "Estonia",
	LV: "Latvia",
	NO: "Norway",
	SE: "Sweden"
};

const COUNTRY_ORDER = ["Estonia", "Latvia", "Norway", "Sweden"];

const PHASE_COLORS = {
	"Permitting": "#2c3e5a",
	"Landowner agreement": "#7a9cb7",
	"Terminated": "#b0c4d8"
};

const WIND_BUDGET = {
	Estonia:	{ secured: 1000, rtb: 400 },
	Latvia:		{ secured: 1300, rtb: 400 },
	Norway:		{ secured: 1300, rtb: 500 },
	Sweden:		{ secured: 800, rtb: 100 }
};

const SOLAR_BUDGET = {
	Estonia:	{ secured: 0, rtb: 0 },
	Latvia:		{ secured: 150, rtb: 50 },
	Norway:		{ secured: 300, rtb: 100 },
	Sweden:		{ secured: 0, rtb: 0 }
};

function formatMonth(dateStr) {
	const d = new Date(dateStr + "T00:00:00");
	return d.toLocaleDateString("en-US", { month: "long" });
}

function formatNumber(num) {
	if (num == null) return "\u2014";
	return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export default function Metrics() {
	const { country: countryCode } = useParams();
	const navigate = useNavigate();

	const [user, setUser] = useState({});
	const [loading, setLoading] = useState(true);

	// Chart view state
	const [windPipeline, setWindPipeline] = useState(null);
	const [solarPipeline, setSolarPipeline] = useState(null);

	// Table view state
	const [tableData, setTableData] = useState(null);
	const [tableEnergyType, setTableEnergyType] = useState("Wind");
	const [tableCountry, setTableCountry] = useState(null);

	const isTableView = !!countryCode;

	useEffect(() => {
		loadData();
	}, []);

	useEffect(() => {
		if (countryCode) {
			const c = COUNTRY_FROM_CODE[countryCode.toUpperCase()];
			if (c) {
				setTableCountry(c);
				loadTableData(c, tableEnergyType);
			}
		}
	}, [countryCode, tableEnergyType]);

	const loadData = () => {
		Promise.all([vindrApi.getToken()])
			.then(() => Promise.all([
				authApi.getUser(),
				vindrApi.getMetricsPipeline("Wind"),
				vindrApi.getMetricsPipeline("Solar")
			]))
			.then((res) => {
				setUser(typeof res[0].json === "function" ? res[0] : res[0]);
				setWindPipeline(res[1]);
				setSolarPipeline(res[2]);
				setLoading(false);
			})
			.catch((e) => {
				console.error("Error loading metrics:", e);
				setLoading(false);
			});
	};

	const loadTableData = (country, energyType) => {
		vindrApi.getToken()
			.then(() => vindrApi.getMetricsProjects(country, energyType))
			.then((data) => setTableData(data))
			.catch((e) => console.error("Error loading table data:", e));
	};

	const switchEnergyType = () => {
		const next = tableEnergyType === "Wind" ? "Solar" : "Wind";
		setTableEnergyType(next);
		setTableData(null);
	};

	// Build chart data from pipeline response
	// Landowner agreement + Permitting stack on positive side, Terminated on negative side
	const buildChartData = (pipeline) => {
		if (!pipeline || !pipeline.countries) return [];
		const dates = [...(pipeline.dates || [])].sort();
		const data = [];

		COUNTRY_ORDER.forEach((country) => {
			const countryData = pipeline.countries[country];
			if (!countryData) return;

			dates.forEach((date, dateIdx) => {
				const vals = countryData[date] || {};
				const terminated = vals["Terminated"] || 0;
				const terminatedVal = terminated < 0 ? terminated : (terminated > 0 ? -terminated : 0);
				data.push({
					country,
					date,
					label: formatMonth(date),
					uniqueLabel: `${country} - ${formatMonth(date)}`,
					countryLabel: country,
					isFirstInGroup: dateIdx === 0,
					"Landowner agreement": vals["Landowner agreement"] || 0,
					"Permitting": vals["Permitting"] || 0,
					"Terminated": terminatedVal,
					"_offset": Math.abs(terminatedVal)
				});
			});
		});
		return data;
	};

	const buildBudgetData = (budgetMap) => {
		return COUNTRY_ORDER.map((country) => ({
			country,
			secured: budgetMap[country]?.secured || 0,
			rtb: budgetMap[country]?.rtb || 0
		}));
	};

	const CustomBarLabel = (props) => {
		const { x, y, width, height, value } = props;
		if (!value || value === 0) return null;
		const isNeg = value < 0;
		return (
			<text
				x={x + width / 2}
				y={isNeg ? y + height + 14 : y + height / 2 + 4}
				fill={isNeg ? "#333" : "#fff"}
				textAnchor="middle"
				fontSize={11}
				fontWeight={500}
			>
				{formatNumber(value)}
			</text>
		);
	};

	const renderPipelineChart = (pipeline, title) => {
		const chartData = buildChartData(pipeline);
		if (!chartData.length) return <div className="text-muted p-3">No data available</div>;

		const dates = [...(pipeline.dates || [])].sort();
		const barSize = 40;
		const barsPerGroup = dates.length;

		// Store tick x-positions so we can center country labels between the two bars
		const tickPositions = {};

		const CustomTick = ({ x, y, payload }) => {
			const idx = payload.index;
			const item = chartData[idx];
			if (!item) return null;

			// Record this tick's x position
			tickPositions[idx] = x;

			// Country label: render on the last bar of each group, centered between first and last
			const isLastInGroup = (idx % barsPerGroup) === barsPerGroup - 1;
			const firstIdx = idx - (barsPerGroup - 1);
			const firstX = tickPositions[firstIdx];
			const centerX = firstX != null ? (firstX + x) / 2 : x;

			return (
				<g transform={`translate(${x},${y})`}>
					<text x={0} y={0} dy={14} textAnchor="middle" fill="#666" fontSize={11}>
						{item.label}
					</text>
					{isLastInGroup && (
						<text
							x={centerX - x}
							y={0}
							dy={30}
							textAnchor="middle"
							fill="#333"
							fontSize={12}
							fontWeight={600}
						>
							{item.countryLabel}
						</text>
					)}
				</g>
			);
		};

		return (
			<Card className="metrics-chart-card">
				<Card.Body>
					<h5 className="text-center mb-3 fw-semibold">{title}</h5>
					<div className="d-flex justify-content-center gap-4 mb-2">
						{Object.entries(PHASE_COLORS).map(([phase, color]) => (
							<div key={phase} className="d-flex align-items-center gap-1">
								<span className="metrics-legend-dot" style={{ backgroundColor: color }} />
								<span className="small">{phase}</span>
							</div>
						))}
					</div>
					<ResponsiveContainer width="100%" height={300}>
						<BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 40 }} barSize={barSize}>
							<XAxis
								dataKey="uniqueLabel"
								tick={<CustomTick />}
								interval={0}
								tickLine={false}
								axisLine={{ stroke: "#ccc" }}
							/>
							<YAxis
								label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0, style: { fontSize: 12 } }}
								tickFormatter={(v) => v.toLocaleString()}
								axisLine={{ stroke: "#ccc" }}
								tickLine={false}
							/>
							<Tooltip
								formatter={(value, name) => {
									if (name === "_offset") return null;
									return [formatNumber(Math.abs(value)) + " MW", name];
								}}
								itemStyle={{ display: "auto" }}
								labelFormatter={(label, payload) => {
									const item = payload && payload.find(p => p.dataKey !== "_offset" && p.payload && p.payload.countryLabel);
									if (item) return `${item.payload.countryLabel} - ${item.payload.label}`;
									return label;
								}}
							/>
							<ReferenceLine y={0} stroke="#ccc" />
							<Bar dataKey="Terminated" stackId="a" fill={PHASE_COLORS["Terminated"]}>
								<LabelList dataKey="Terminated" content={<CustomBarLabel />} />
							</Bar>
							<Bar dataKey="_offset" stackId="a" fill="transparent" isAnimationActive={false} />
							<Bar dataKey="Landowner agreement" stackId="a" fill={PHASE_COLORS["Landowner agreement"]}>
								<LabelList dataKey="Landowner agreement" content={<CustomBarLabel />} />
							</Bar>
							<Bar dataKey="Permitting" stackId="a" fill={PHASE_COLORS["Permitting"]} radius={[3, 3, 0, 0]}>
								<LabelList dataKey="Permitting" content={<CustomBarLabel />} />
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</Card.Body>
			</Card>
		);
	};

	const renderBudgetChart = (budgetMap, title) => {
		const data = buildBudgetData(budgetMap);
		return (
			<Card className="metrics-chart-card">
				<Card.Body>
					<h5 className="text-center mb-3 fw-semibold">{title}</h5>
					<div className="d-flex justify-content-center gap-4 mb-2">
						<div className="d-flex align-items-center gap-1">
							<span className="metrics-legend-dot" style={{ backgroundColor: "#b0c4d8" }} />
							<span className="small">Secured (2025)</span>
						</div>
						<div className="d-flex align-items-center gap-1">
							<span className="metrics-legend-dot" style={{ backgroundColor: "#2c3e5a" }} />
							<span className="small">RTB (2030)</span>
						</div>
					</div>
					<ResponsiveContainer width="100%" height={300}>
						<BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }} barGap={8} barCategoryGap="20%">
							<XAxis dataKey="country" tickLine={false} axisLine={{ stroke: "#ccc" }} fontSize={11} interval={0} />
							<YAxis
								tickFormatter={(v) => v.toLocaleString()}
								axisLine={{ stroke: "#ccc" }}
								tickLine={false}
							/>
							<Tooltip formatter={(value) => [formatNumber(value) + " MW"]} />
							<Bar dataKey="secured" fill="#b0c4d8" barSize={22}>
								<LabelList dataKey="secured" position="top" fontSize={10} />
							</Bar>
							<Bar dataKey="rtb" fill="#2c3e5a" barSize={22}>
								<LabelList dataKey="rtb" position="top" fontSize={10} />
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</Card.Body>
			</Card>
		);
	};

	const getScreeningMW = (pipeline, country) => {
		if (!pipeline?.countries?.[country]) return 0;
		const countryData = pipeline.countries[country];
		const dates = [...(pipeline.dates || [])].sort().reverse();
		const latestDate = dates[0];
		if (!latestDate || !countryData[latestDate]) return 0;
		return countryData[latestDate]["Screening"] || 0;
	};

	const renderChartView = () => {
		return (
			<div className="metrics-dashboard">
				<Row style={{ marginTop: '8px' }}>
					{/* Left column - country buttons and screening boxes */}
					<Col xs={12} lg={2} className="mb-4">
						<p className="text-muted small mb-2">Click to see project details:</p>
						<div className="d-flex gap-2 mb-4 flex-wrap">
							{COUNTRY_ORDER.map((country) => (
								<button
									key={country}
									className="metrics-country-btn"
									onClick={() => navigate(`/pages/metrics/${COUNTRY_CODES[country]}`)}
								>
									{COUNTRY_CODES[country]}
								</button>
							))}
						</div>
						{COUNTRY_ORDER.map((country) => {
							const screeningMW = getScreeningMW(windPipeline, country);
							return (
								<Card key={country} className="metrics-screening-card mb-3">
									<Card.Body className="text-center py-3">
										<div className="small text-muted">Projects in screening {country}</div>
										<div className="metrics-screening-value">{Math.round(screeningMW).toLocaleString()} <span className="small text-muted">MW</span></div>
									</Card.Body>
								</Card>
							);
						})}
					</Col>

					{/* Center - pipeline charts */}
					<Col xs={12} lg={6} className="mb-4">
						{renderPipelineChart(windPipeline, "Wind projects in Pipeline")}
						<div className="mt-3">
							{renderPipelineChart(solarPipeline, "Solar projects in Pipeline")}
						</div>
					</Col>

					{/* Right - budget charts */}
					<Col xs={12} lg={4} className="mb-4">
						{renderBudgetChart(WIND_BUDGET, "MW wind budget")}
						<div className="mt-3">
							{renderBudgetChart(SOLAR_BUDGET, "MW solar budget")}
						</div>
					</Col>
				</Row>
			</div>
		);
	};

	const renderDeltaIcon = (delta) => {
		if (delta == null) return null;
		if (delta === 0) return null;
		if (delta > 0) return <span className="text-success" style={{ fontSize: 11 }}>&#9650;</span>;
		return <span className="text-danger" style={{ fontSize: 11 }}>&#9660;</span>;
	};

	const tableColumns = [
		{ name: "Project ID", selector: (r) => r.projectid, sortable: true, compact: true, grow: 0, width: "72px" },
		{ name: "Name", selector: (r) => r.name, sortable: true, grow: 1, cell: (r) => r._isTotal ? <strong>Total</strong> : r.name },
		{ name: <span>Price<br />region</span>, selector: (r) => r.price_region || "", sortable: true, compact: true, grow: 0, width: "60px" },
		{ name: "Status", selector: (r) => r.phase, sortable: true, grow: 1 },
		{ name: "Year", selector: (r) => r.vintage, sortable: true, compact: true, grow: 0, width: "50px" },
		{
			name: <div className="w-100 text-center">Signed<br />contracts<br /><small>Last month</small></div>,
			selector: (r) => r.compare_signed_contracts,
			sortable: true, compact: true, center: true, grow: 0, width: "75px",
			cell: (r) => formatNumber(r.compare_signed_contracts)
		},
		{
			name: <div className="w-100 text-center">Signed<br />contracts</div>,
			selector: (r) => r.signed_contracts, sortable: true, compact: true, center: true, grow: 0, width: "75px"
		},
		{
			name: <div className="w-100 text-center">Project potential<br />MW</div>,
			selector: (r) => r.project_potential_mw,
			sortable: true, compact: true, center: true, grow: 0, width: "80px",
			cell: (r) => formatNumber(r.project_potential_mw)
		},
		{
			name: <div className="w-100 text-center">Signed capacity<br /><small>Last month</small></div>,
			selector: (r) => r.compare_signed_capacity_mw,
			sortable: true, compact: true, center: true, grow: 0, width: "80px",
			cell: (r) => formatNumber(r.compare_signed_capacity_mw)
		},
		{
			name: <div className="w-100 text-center">Signed capacity<br />MW</div>,
			selector: (r) => r.signed_capacity_mw,
			sortable: true, compact: true, center: true, grow: 0, width: "90px",
			cell: (r) => (
				<span>
					{formatNumber(r.signed_capacity_mw)} {renderDeltaIcon(r.signed_capacity_mw_delta)}
				</span>
			)
		},
		{
			name: <div className="w-100 text-center">M1 approved<br />MW</div>,
			selector: (r) => r.capacity_approved_mw,
			sortable: true, compact: true, center: true, grow: 0, width: "80px",
			cell: (r) => formatNumber(r.capacity_approved_mw)
		}
	];

	const filterProjects = (projects) => {
		return (projects || [])
			.filter(p => p.phase === "Landowner agreement" || p.phase === "Permitting")
			.map(p => (p.state === "On hold" || p.state === "Terminated") ? { ...p, phase: "Terminated" } : p);
	};

	const buildTotalsRow = (projects) => {
		if (!projects.length) return null;
		return {
			_isTotal: true,
			projectid: "",
			name: "Total",
			price_region: "",
			phase: "",
			vintage: "",
			compare_signed_contracts: projects.reduce((s, p) => s + (p.compare_signed_contracts || 0), 0),
			signed_contracts: projects.reduce((s, p) => s + (p.signed_contracts || 0), 0),
			project_potential_mw: projects.reduce((s, p) => s + (p.project_potential_mw || 0), 0),
			compare_signed_capacity_mw: projects.reduce((s, p) => s + (p.compare_signed_capacity_mw || 0), 0),
			signed_capacity_mw: projects.reduce((s, p) => s + (p.signed_capacity_mw || 0), 0),
			signed_capacity_mw_delta: projects.reduce((s, p) => s + (p.signed_capacity_mw_delta || 0), 0),
			capacity_approved_mw: projects.reduce((s, p) => s + (p.capacity_approved_mw || 0), 0)
		};
	};

	const otherCountryCodes = Object.entries(COUNTRY_CODES)
		.filter(([name]) => name !== tableCountry)
		.map(([name, code]) => ({ name, code }));

	const renderTableView = () => {
		const filtered = filterProjects(tableData?.projects);
		const totalsRow = buildTotalsRow(filtered);
		const tableRows = totalsRow ? [...filtered, totalsRow] : filtered;
		const devCount = filtered.filter(p => p.phase !== "Terminated").length || 0;

		return (
			<div className="metrics-table-view">
				<div className="d-flex align-items-start justify-content-between mb-4">
					<div className="d-flex align-items-center gap-3">
						<button className="metrics-back-btn" onClick={() => navigate("/pages/metrics")}>
							<ArrowLeft size={20} weight="bold" />
						</button>
						<div>
							<h3 className="mb-0 fw-bold">Project Status</h3>
							<h4 className="mb-0 text-muted">{tableCountry} - {tableEnergyType}</h4>
						</div>
					</div>
					<div className="d-flex align-items-center gap-3">
						<span className="text-muted small">Report last updated: {new Date().toLocaleString()}</span>
						<Button variant="dark" className="d-flex align-items-center gap-2" onClick={switchEnergyType}>
							Go to {tableEnergyType === "Wind" ? "solar" : "wind"} projects <ArrowRight size={16} />
						</Button>
						<div className="d-flex gap-2">
							{otherCountryCodes.map(({ name, code }) => (
								<button
									key={code}
									className="metrics-country-btn"
									onClick={() => navigate(`/pages/metrics/${code}`)}
								>
									{code}
								</button>
							))}
						</div>
					</div>
				</div>

				{!tableData ? (
					<div className="text-center p-5"><Spinner animation="border" variant="success" /></div>
				) : (
					<>
						<DataTable
							columns={tableColumns}
							data={tableRows}
							dense
							highlightOnHover
							noDataComponent="No projects found"
							defaultSortFieldId={4}
							sortFunction={(rows, selector, direction) => {
								return rows.sort((a, b) => {
									if (a._isTotal) return 1;
									if (b._isTotal) return -1;
									const aVal = selector(a);
									const bVal = selector(b);
									let cmp = 0;
									if (aVal == null) cmp = -1;
									else if (bVal == null) cmp = 1;
									else if (typeof aVal === "string") cmp = aVal.localeCompare(bVal);
									else cmp = aVal - bVal;
									return direction === "asc" ? cmp : -cmp;
								});
							}}
							conditionalRowStyles={[{
								when: (row) => row._isTotal,
								style: { fontWeight: 700, borderTop: "2px solid #dee2e6" }
							}]}
							customStyles={{
								headCells: {
									style: {
										fontWeight: 600,
										fontSize: "12px",
										color: "#333",
										paddingLeft: "6px",
										paddingRight: "6px",
										textAlign: "center"
									}
								},
								cells: {
									style: {
										fontSize: "12px",
										paddingLeft: "6px",
										paddingRight: "6px"
									}
								},
								rows: {
									style: {
										minHeight: "32px"
									}
								}
							}}
						/>
						<p className="metrics-footer-text mt-4">
							We are currently developing {devCount} {tableEnergyType.toLowerCase()} project(s)!
						</p>
					</>
				)}
			</div>
		);
	};

	return (
		<React.Fragment>
			<div className="header">
				<div className="menu"><i className="ri-menu-2-fill"></i></div>
				<div className="middle">
					<Link to="/" className="header-logo">Site Planner</Link>
				</div>
				<div className="logo">
					<img className="img-fluid d-none d-md-block brand-logo" src="/images/vindr_logo_blue.svg" alt="Vindr" />
				</div>
				<div className="options">
					{user.userId && <UserMenu user={user} onUpdateUser={setUser} />}
				</div>
			</div>

			<div className="body dashboard2">
				<Sidebar />
				<div className="main metrics-main p-4 p-lg-5">
					{isTableView ? (
						renderTableView()
					) : loading ? (
						<div className="text-center p-5"><Spinner animation="border" variant="success" size="xl" /></div>
					) : (
						renderChartView()
					)}
				</div>
			</div>
		</React.Fragment>
	);
}
